import React, { useEffect } from 'react'
import { Row, Col, Card, Button, Badge } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Loader from '../components/Loader'
import Message from '../components/Message'
import { listTournaments, deleteTournament } from '../actions/tournamentActions'

const STATUS_VARIANT = {
    draft:     'secondary',
    upcoming:  'primary',
    active:    'success',
    completed: 'dark',
    cancelled: 'danger',
}

const STATUS_BORDER = {
    draft:     '#6c757d',
    upcoming:  '#0d6efd',
    active:    '#198754',
    completed: '#212529',
    cancelled: '#dc3545',
}

function TournamentListScreen() {
    const dispatch  = useDispatch()
    const navigate  = useNavigate()

    const tournamentList = useSelector(state => state.tournamentList)
    const { loading, error, tournaments } = tournamentList

    const tournamentDelete = useSelector(state => state.tournamentDelete)
    const { success: successDelete } = tournamentDelete

    useEffect(() => {
        dispatch(listTournaments())
    }, [successDelete])

    const deleteHandler = (id) => {
        if (window.confirm('Delete this tournament? This cannot be undone.')) {
            dispatch(deleteTournament(id))
        }
    }

    return (
        <>
            <Row className='align-items-center mb-3'>
                <Col><h2>Tournaments</h2></Col>
                <Col className='text-end'>
                    <Button variant='dark' size='sm' onClick={() => navigate('/admin/tournaments/create')}>
                        <i className='fa-solid fa-plus me-1'></i> Add Tournament
                    </Button>
                </Col>
            </Row>

            {loading && <Loader />}
            {error && <Message variant='danger'>{error}</Message>}

            {!loading && !error && (
                <>
                    {(!tournaments || tournaments.length === 0) ? (
                        <p className='text-muted'>No tournaments yet. Click Add Tournament to get started.</p>
                    ) : (() => {
                        // Sort helpers — nulls always last
                        const byDate = (a, b, asc = true) => {
                            if (!a.start_date && !b.start_date) return 0
                            if (!a.start_date) return 1
                            if (!b.start_date) return -1
                            const cmp = a.start_date.localeCompare(b.start_date)
                            return asc ? cmp : -cmp
                        }

                        const upcoming = tournaments
                            .filter(t => t.status !== 'completed')
                            .sort((a, b) => byDate(a, b, true))   // nearest first

                        const completed = tournaments
                            .filter(t => t.status === 'completed')
                            .sort((a, b) => byDate(a, b, false))  // most recent first

                        const TournamentCard = (t) => (
                            <Col key={t.id}>
                                <Card
                                    className='h-100 shadow-sm'
                                    style={{ borderLeft: `4px solid ${STATUS_BORDER[t.status] || '#6c757d'}` }}
                                >
                                    <Card.Body>
                                        <div className='d-flex justify-content-between align-items-start mb-2'>
                                            <Badge bg={STATUS_VARIANT[t.status] || 'secondary'}>
                                                {t.status?.charAt(0).toUpperCase() + t.status?.slice(1)}
                                            </Badge>
                                            <small className='text-muted border rounded px-2 py-0' style={{ fontSize: '0.7rem' }}>
                                                {t.type}
                                            </small>
                                        </div>

                                        <Card.Title className='mb-1 fs-6 fw-bold'>{t.name}</Card.Title>

                                        {t.start_date && (
                                            <p className='text-muted small mb-1'>
                                                <i className='fa-solid fa-calendar me-1'></i>
                                                {new Date(t.start_date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </p>
                                        )}

                                        {t.location && (
                                            <p className='text-muted small mb-2'>
                                                <i className='fa-solid fa-location-dot me-1'></i>{t.location}
                                            </p>
                                        )}

                                        <div className='d-flex flex-wrap gap-3 small text-muted mb-3'>
                                            <span title='Format'>
                                                <i className='fa-solid fa-users me-1'></i>{t.teams}
                                            </span>
                                            <span title='Entry Fee'>
                                                <i className='fa-solid fa-dollar-sign me-1'></i>{t.entry_fee}
                                            </span>
                                            <span title='Rounds'>
                                                <i className='fa-solid fa-flag-checkered me-1'></i>
                                                {t.days_count} round{t.days_count !== 1 ? 's' : ''}
                                            </span>
                                            {t.max_players && (
                                                <span title='Max Players'>
                                                    <i className='fa-solid fa-person me-1'></i>{t.max_players} max
                                                </span>
                                            )}
                                        </div>

                                        <div className='d-flex gap-2'>
                                            <Button
                                                variant='outline-secondary'
                                                size='sm'
                                                onClick={() => navigate(`/admin/tournaments/${t.id}/edit`)}
                                            >
                                                <i className='fa-solid fa-pen me-1'></i>Edit
                                            </Button>
                                            <Button
                                                variant='outline-danger'
                                                size='sm'
                                                onClick={() => deleteHandler(t.id)}
                                            >
                                                <i className='fa-solid fa-trash'></i>
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )

                        return (
                            <>
                                {/* ── Upcoming / Active ───────────────────── */}
                                {upcoming.length > 0 && (
                                    <>
                                        <h5 className='text-muted fw-semibold mb-3' style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                            <i className='fa-solid fa-calendar-days me-2'></i>Upcoming &amp; Active
                                        </h5>
                                        <Row xs={1} sm={2} md={3} className='g-3 mb-4'>
                                            {upcoming.map(t => TournamentCard(t))}
                                        </Row>
                                    </>
                                )}

                                {/* ── Completed ───────────────────────────── */}
                                {completed.length > 0 && (
                                    <>
                                        <hr className='mb-3' />
                                        <h5 className='text-muted fw-semibold mb-3' style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                            <i className='fa-solid fa-flag-checkered me-2'></i>Completed
                                        </h5>
                                        <Row xs={1} sm={2} md={3} className='g-3'>
                                            {completed.map(t => TournamentCard(t))}
                                        </Row>
                                    </>
                                )}
                            </>
                        )
                    })()}
                </>
            )}
        </>
    )
}

export default TournamentListScreen
